import { useState } from "react";
import axios from "axios";
import { Mail, Send, Check, AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/contact/submit`, form);
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke sende — prøv igen om lidt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 md:py-20">
      <SEO
        title="Kontakt | MinFesttale.dk"
        description="Skriv til os om alt fra spørgsmål til feedback. Vi svarer inden for 24 timer på hverdage."
        path="/kontakt"
      />
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-full bg-secondary/15 flex items-center justify-center mx-auto mb-4">
            <Mail className="h-6 w-6 text-secondary" />
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-primary">
            Kontakt os
          </h1>
          <p className="text-muted-foreground mt-3">
            Spørgsmål eller feedback? Vi svarer typisk inden for 24 timer.
          </p>
        </div>

        {sent ? (
          <div
            className="bg-white rounded-2xl border border-accent/30 shadow-md p-8 text-center"
            data-testid="contact-success"
          >
            <div className="w-14 h-14 rounded-full bg-accent/15 flex items-center justify-center mx-auto mb-4">
              <Check className="h-7 w-7 text-accent" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-primary mb-2">
              Tak for din besked!
            </h2>
            <p className="text-muted-foreground">
              Vi vender tilbage til <strong>{form.email}</strong> inden for 24 timer.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl border border-border/40 shadow-md p-6 md:p-8 space-y-5"
            data-testid="contact-form"
          >
            <div>
              <label htmlFor="contact-name" className="text-sm font-medium text-primary mb-1 block">
                Navn
              </label>
              <input
                id="contact-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-border bg-muted/30 text-primary focus:outline-none focus:ring-2 focus:ring-secondary"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="text-sm font-medium text-primary mb-1 block">
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-border bg-muted/30 text-primary focus:outline-none focus:ring-2 focus:ring-secondary"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="text-sm font-medium text-primary mb-1 block">
                Besked
              </label>
              <textarea
                id="contact-message"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                minLength={5}
                maxLength={5000}
                className="w-full px-4 py-3 rounded-lg border border-border bg-muted/30 text-primary focus:outline-none focus:ring-2 focus:ring-secondary resize-y"
                data-testid="contact-message-input"
              />
            </div>

            {/* Honeypot — hidden from real users */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="absolute opacity-0 pointer-events-none h-0 w-0"
              aria-hidden="true"
            />

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="contact-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/90 text-primary font-medium py-3 rounded-full shadow transition-all disabled:opacity-50"
              data-testid="contact-submit"
            >
              {loading ? "Sender..." : (<><Send className="h-4 w-4" /> Send besked</>)}
            </button>

            <p className="text-xs text-muted-foreground text-center">
              Eller skriv direkte til <a href="mailto:hej@minfesttale.dk" className="underline">hej@minfesttale.dk</a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
